<script setup>
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'

// Nights at sea (go-to-market 2026-10-24): three formats told as routes and
// seasons, the boats that have cabins with their night rate, how a voyage is
// priced, the questions people ask, and the plan form prefilled as a
// multi-day request. All copy arrives in `content` from voyages.*.yml so the
// page, its markdown twin and the FAQ JSON-LD say the same thing.
defineOptions({ layout: PublicLayout })

const { t } = useI18n()
defineProps({
  content: { type: Object, required: true },  // title, lede, eyebrow, formats[], included[], how[], faq[], labels…
  boats:   { type: Array,  default: () => [] }, // { id, title, length_ft, capacity, cabins, crew, tier, night_price, day_price, photo_url }
  paths:   { type: Object, required: true }   // charters, occasions, plan
})

function fill(template, vars) {
  return String(template || '').replace(/%\{(\w+)\}/g, (_, k) => (vars[k] ?? ''))
}
</script>

<template>
  <Head>
    <title>{{ content.title }} · {{ t('public.charter_home.head_title') }}</title>
    <meta name="description" :content="content.lede">
  </Head>

  <BrandHero kind="charter" :eyebrow="content.eyebrow" :title="content.title">
    <p class="bhero-lede">{{ content.lede }}</p>
    <div class="bhero-ctas">
      <LuxButton :href="paths.plan" variant="solid" tone="dark" glyph="arrow">{{ content.plan_cta }}</LuxButton>
      <LuxButton :href="paths.charters" variant="line" tone="dark">{{ content.all_boats }}</LuxButton>
    </div>
  </BrandHero>

  <section class="body">
    <div class="inner">
      <section class="block">
        <p class="eyebrow">{{ content.formats_title }}</p>
        <ul class="formats">
          <li v-for="f in content.formats" :key="f.slug" class="format">
            <span class="format__nights mono">{{ fill(content.nights, { n: f.nights }) }}</span>
            <h2 class="format__title">{{ f.title }}</h2>
            <p class="format__route">{{ f.route }}</p>
            <p class="format__meta"><strong>{{ content.season_label }}</strong> {{ f.season }}</p>
            <p class="format__meta"><strong>{{ content.best_for_label }}</strong> {{ f.best_for }}</p>
          </li>
        </ul>
      </section>

      <div class="arrange">
        <div>
          <p class="eyebrow">{{ content.included_title }}</p>
          <ul class="arrange__list">
            <li v-for="(item, i) in content.included" :key="i">{{ item }}</li>
          </ul>
        </div>
        <aside class="plan-card">
          <p class="plan-card__text">{{ content.plan_band }}</p>
          <LuxButton :href="paths.plan" variant="solid" tone="light" glyph="arrow">{{ content.plan_cta }}</LuxButton>
        </aside>
      </div>

      <section class="block">
        <div class="block__head">
          <div>
            <h2 class="h2">{{ content.boats_title }}</h2>
            <p class="note">{{ content.boats_note }}</p>
          </div>
          <a :href="paths.charters" class="more">{{ content.all_boats }} →</a>
        </div>
        <ul v-if="boats.length" class="grid">
          <li v-for="b in boats" :key="b.id">
            <a :href="`${paths.charters}/${b.id}`" class="boat">
              <div class="boat__frame">
                <div class="boat__photo" :class="{ 'boat__photo--empty': !b.photo_url }" :style="b.photo_url ? { backgroundImage: `url(${b.photo_url})` } : null" />
                <span v-if="b.tier && b.tier !== 'standard'" class="boat__pill">{{ t(`public.tiers.${b.tier}`) }}</span>
              </div>
              <h3 class="boat__title">{{ b.title }}</h3>
              <p class="boat__meta">
                <span v-if="b.length_ft">{{ fill(content.length, { n: b.length_ft }) }}</span>
                <span>{{ fill(content.cabins, { n: b.cabins }) }}</span>
                <span>{{ fill(content.guests, { n: b.capacity }) }}</span>
              </p>
              <p class="boat__price fw-value">{{ b.night_price ? fill(content.per_night, { price: b.night_price }) : content.on_request }}</p>
            </a>
          </li>
        </ul>
        <p v-else class="empty">{{ content.boats_empty }} <a :href="paths.plan" class="more">{{ content.plan_cta }} →</a></p>
      </section>

      <div class="two">
        <section class="block block--card">
          <h2 class="h2 h2--small">{{ content.how_title }}</h2>
          <ol class="how">
            <li v-for="(h, i) in content.how" :key="i"><span class="how__n mono">{{ i + 1 }}</span><span class="how__text">{{ h }}</span></li>
          </ol>
        </section>
        <section class="block block--card">
          <h2 class="h2 h2--small">{{ content.faq_title }}</h2>
          <details v-for="(f, i) in content.faq" :key="i" class="faq">
            <summary class="faq__q">{{ f.q }}</summary>
            <p class="faq__a">{{ f.a }}</p>
          </details>
        </section>
      </div>

      <div class="plan-band">
        <p>{{ content.plan_band }}</p>
        <LuxButton :href="paths.plan" variant="solid" tone="light" glyph="arrow">{{ content.plan_cta }}</LuxButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 54ch; opacity: 0.94; }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }

.body { background: var(--bg); }
.inner { max-width: 80rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; display: grid; gap: clamp(2.5rem, 5vw, 4rem); }
.eyebrow { margin: 0 0 0.75rem; font-size: 0.75rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 700; color: var(--accent); }
.h2 { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: clamp(1.8rem, 3.2vw, 2.6rem); line-height: 1.05; margin: 0; color: var(--ink); text-wrap: balance; }
.h2--small { font-size: 1.7rem; margin-bottom: 1rem; }
.note { margin: 0.5rem 0 0; color: var(--inkSoft); max-width: 60ch; line-height: 1.5; }
.more { font-size: 0.9rem; font-weight: 700; color: var(--azure, var(--second)); text-decoration: none; white-space: nowrap; }
.more:hover { text-decoration: underline; }
.mono { font-family: var(--font-mono, var(--mono)); }

.formats { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr)); gap: 1.25rem; }
.format { background: var(--surface); border: 1px solid var(--line); border-radius: 1.25rem; padding: 1.5rem; display: grid; gap: 0.6rem; align-content: start; }
.format__nights { font-size: 0.78rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--accent); font-weight: 700; }
.format__title { margin: 0; font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.5rem; line-height: 1.15; color: var(--ink); }
.format__route { margin: 0; color: var(--inkSoft); line-height: 1.55; }
.format__meta { margin: 0; font-size: 0.92rem; color: var(--inkSoft); line-height: 1.5; }
.format__meta strong { color: var(--ink); font-weight: 600; }

.arrange { display: grid; grid-template-columns: 1.4fr 1fr; gap: 2rem; align-items: start; }
.arrange__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.7rem; }
.arrange__list li { position: relative; padding-left: 1.8rem; font-size: 1.08rem; line-height: 1.5; color: var(--ink); }
.arrange__list li::before { content: "✓"; position: absolute; left: 0; top: 0.05rem; color: var(--ok); font-weight: 700; }
.plan-card { background: linear-gradient(160deg, var(--sand, var(--surfaceAlt)) 0%, var(--accentSoft) 100%); border-radius: 1.25rem; padding: 1.5rem; display: grid; gap: 1rem; justify-items: start; }
.plan-card__text { margin: 0; font-family: var(--font-display, var(--display)); font-size: 1.35rem; line-height: 1.25; color: var(--ink); }

.block__head { display: flex; justify-content: space-between; align-items: end; gap: 1rem; margin-bottom: 1.25rem; }
.grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr)); gap: 1.25rem; }
.empty { color: var(--inkSoft); margin: 0; display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: center; }
.boat { display: grid; gap: 0.45rem; text-decoration: none; color: inherit; }
.boat__frame { position: relative; border-radius: 1rem; overflow: hidden; aspect-ratio: 4 / 3; background: var(--surfaceAlt); }
.boat__photo { position: absolute; inset: 0; background-size: cover; background-position: center; transition: transform 0.4s ease; }
.boat:hover .boat__photo { transform: scale(1.03); }
.boat__photo--empty { background: linear-gradient(160deg, var(--surfaceAlt), var(--accentSoft)); }
.boat__pill { position: absolute; top: 0.75rem; left: 0.75rem; padding: 0.3rem 0.7rem; border-radius: 999px; background: var(--ink); color: var(--surface); font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; font-weight: 700; }
.boat__title { margin: 0.35rem 0 0; font-size: 1.1rem; font-weight: 600; color: var(--ink); line-height: 1.3; }
.boat__meta { margin: 0; display: flex; flex-wrap: wrap; gap: 0.35rem 0.9rem; font-size: 0.88rem; color: var(--inkSoft); }
.boat__price { margin: 0; color: var(--ink); }
.block--card { background: var(--surface); border: 1px solid var(--line); border-radius: 1.25rem; padding: 1.5rem; }
.two { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; align-items: start; }
.how { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.75rem; }
.how li { display: grid; grid-template-columns: 2.2rem 1fr; gap: 0.75rem; border-top: 1px solid var(--line); padding-top: 0.75rem; }
.how li:first-child { border-top: 0; padding-top: 0; }
.how__n { font-size: 0.8rem; letter-spacing: 0.04em; color: var(--accent); padding-top: 0.2rem; }
.how__text { color: var(--inkSoft); line-height: 1.5; }
.faq { border-top: 1px solid var(--line); padding: 0.75rem 0; }
.faq:first-of-type { border-top: 0; padding-top: 0; }
.faq__q { cursor: pointer; font-weight: 600; color: var(--ink); }
.faq__a { margin: 0.5rem 0 0; color: var(--inkSoft); line-height: 1.55; }
.plan-band { padding: 1.5rem 1.75rem; border-radius: 1.25rem; background: linear-gradient(160deg, var(--sand, var(--surfaceAlt)) 0%, var(--accentSoft) 100%); display: flex; flex-wrap: wrap; gap: 1rem; align-items: center; justify-content: space-between; }
.plan-band p { margin: 0; color: var(--ink); font-family: var(--font-display, var(--display)); font-size: 1.35rem; line-height: 1.2; max-width: 40ch; }

@media (max-width: 56rem) {
  .arrange, .two { grid-template-columns: 1fr; }
  .block__head { flex-direction: column; align-items: start; }
}
</style>
